import { useEffect, useMemo, useState } from 'react'
import menuService from '../../services/menuService'
import {
  HiOutlinePlus, HiOutlineX, HiOutlinePencil, HiOutlineTrash,
  HiOutlineEye, HiOutlineEyeOff, HiOutlineExternalLink, HiOutlineChevronRight,
} from 'react-icons/hi'
import toast from 'react-hot-toast'

function MenuManagement() {
  const [tree, setTree] = useState({ header: [], footer: [] })
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // item | 'new' | null

  const load = async () => {
    setLoading(true)
    try {
      const res = await menuService.list()
      if (res.status === 'success') setTree({ header: res.data.header || [], footer: res.data.footer || [] })
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const parentOptions = useMemo(
    () => (tree.header || []).map((i) => ({ id: i.id, label: i.label })),
    [tree],
  )

  const remove = async (item) => {
    if (!window.confirm(`Xóa mục "${item.label}"${item.children?.length ? ' và các mục con' : ''}?`)) return
    try {
      await menuService.remove(item.id)
      toast.success('Đã xóa mục menu')
      load()
    } catch { /* interceptor */ }
  }

  const toggle = async (item) => {
    try { await menuService.update(item.id, { is_active: !item.is_active }); load() } catch { /* interceptor */ }
  }

  const Node = ({ item, depth = 0 }) => (
    <>
      <div className="flex items-center gap-3 py-2.5 px-3 hover:bg-secondary-50/60 rounded-lg group"
        style={{ paddingLeft: `${12 + depth * 24}px` }}>
        {depth > 0 && <HiOutlineChevronRight className="w-4 h-4 text-secondary-300 -ml-5" />}
        <span className="text-2xs text-secondary-300 w-6 text-right">{item.sort_order}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-secondary-900 truncate">
            {item.label}
            {!item.is_active && <span className="ml-2 badge-neutral">ẩn</span>}
          </div>
          <a href={item.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-2xs text-primary-600 hover:underline">
            {item.url} <HiOutlineExternalLink className="w-3 h-3" />
            {item.target === '_blank' && <span className="text-secondary-300">· tab mới</span>}
          </a>
        </div>
        <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100">
          <button onClick={() => toggle(item)} className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title={item.is_active ? 'Ẩn' : 'Hiện'}>
            {item.is_active ? <HiOutlineEyeOff className="w-4 h-4" /> : <HiOutlineEye className="w-4 h-4" />}
          </button>
          <button onClick={() => setEditing(item)} className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Sửa">
            <HiOutlinePencil className="w-4 h-4" />
          </button>
          <button onClick={() => remove(item)} className="p-2 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Xóa">
            <HiOutlineTrash className="w-4 h-4" />
          </button>
        </div>
      </div>
      {item.children?.map((c) => <Node key={c.id} item={c} depth={depth + 1} />)}
    </>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="page-title">Menu điều hướng</h1>
          <p className="page-subtitle">Cấu hình các mục menu (cây) hiển thị trên đầu trang — tên, liên kết, thứ tự</p>
        </div>
        <button onClick={() => setEditing('new')} className="btn-primary">
          <HiOutlinePlus className="w-5 h-5" /> Thêm mục
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
      ) : (
        <div className="card p-2">
          <div className="px-3 py-2 text-2xs uppercase tracking-wide text-secondary-400">Menu đầu trang (header)</div>
          {tree.header.length === 0 ? (
            <div className="text-center py-12 text-secondary-400">Chưa có mục menu nào</div>
          ) : tree.header.map((item) => <Node key={item.id} item={item} />)}
        </div>
      )}

      {editing && (
        <MenuFormModal
          item={editing === 'new' ? null : editing}
          parentOptions={parentOptions}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}

function MenuFormModal({ item, parentOptions, onClose, onSaved }) {
  const isNew = !item
  const [form, setForm] = useState({
    label: item?.label || '',
    url: item?.url || '#',
    parent_id: item?.parent_id || '',
    target: item?.target || '_self',
    sort_order: item?.sort_order ?? 0,
    is_active: item?.is_active ?? true,
    location: item?.location || 'header',
  })
  const [saving, setSaving] = useState(false)
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  // không cho chọn chính nó làm cha
  const parents = parentOptions.filter((p) => !item || p.id !== item.id)

  const save = async () => {
    if (!form.label.trim()) { toast.error('Vui lòng nhập tên hiển thị'); return }
    setSaving(true)
    try {
      const payload = {
        label: form.label.trim(),
        url: form.url.trim() || '#',
        parent_id: form.parent_id === '' ? null : Number(form.parent_id),
        target: form.target,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
        location: form.location,
      }
      if (isNew) { await menuService.create(payload); toast.success('Đã thêm mục menu') }
      else { await menuService.update(item.id, payload); toast.success('Đã cập nhật') }
      onSaved()
    } catch { /* interceptor */ } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative card w-full max-w-md p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">{isNew ? 'Thêm mục menu' : 'Sửa mục menu'}</h3>
          <button onClick={onClose} className="p-1.5 text-secondary-400 hover:text-secondary-700">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Tên hiển thị <span className="text-red-500">*</span></label>
            <input className="input" value={form.label} onChange={(e) => setField('label', e.target.value)} placeholder="VD: Tuyển dụng" />
          </div>
          <div>
            <label className="label">Liên kết (redirect)</label>
            <input className="input" value={form.url} onChange={(e) => setField('url', e.target.value)}
              placeholder="#form · /trang · https://..." />
            <p className="text-2xs text-secondary-400 mt-1">Dùng <code>#form</code>, <code>#contact</code> để cuộn trong trang; hoặc URL ngoài.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Mục cha</label>
              <select className="select" value={form.parent_id} onChange={(e) => setField('parent_id', e.target.value)}>
                <option value="">— Mục gốc —</option>
                {parents.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Mở ở</label>
              <select className="select" value={form.target} onChange={(e) => setField('target', e.target.value)}>
                <option value="_self">Cùng tab</option>
                <option value="_blank">Tab mới</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Thứ tự</label>
              <input type="number" className="input" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} />
            </div>
            <div>
              <label className="label">Trạng thái</label>
              <select className="select" value={form.is_active ? '1' : '0'} onChange={(e) => setField('is_active', e.target.value === '1')}>
                <option value="1">Hiển thị</option>
                <option value="0">Ẩn</option>
              </select>
            </div>
          </div>

          <button onClick={save} disabled={saving} className="btn-primary w-full mt-2">
            {saving ? 'Đang lưu...' : (isNew ? 'Thêm mục' : 'Lưu thay đổi')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default MenuManagement
